import {
  CreateBase,
  Form,
  useGetIdentity,
  useListContext,
  useNotify,
  useRecordContext,
  useResourceContext,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/admin/form";
import { cn } from "@/lib/utils";

import { todayIsoDate } from "../misc/formatDate";
import { foreignKeyMapping } from "./foreignKeyMapping";
import { NoteInputs } from "./NoteInputs";
import { toNoteDate } from "./noteModel";

/**
 * The "add a note" form shown on the contact and sponsoring show pages.
 * The resource comes from the surrounding list (contact_notes / sponsoring_notes).
 */
export const NoteCreate = ({
  reference,
  className,
}: {
  reference: "contacts" | "sponsorings";
  className?: string;
}) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (!record || !identity) return null;

  return (
    <CreateBase resource={resource} redirect={false}>
      <Form>
        <div className={cn("flex flex-col gap-3", className)}>
          <NoteInputs />
          <NoteCreateButton record={record} reference={reference} />
        </div>
      </Form>
    </CreateBase>
  );
};

const NoteCreateButton = ({
  reference,
  record,
}: {
  reference: "contacts" | "sponsorings";
  record: RaRecord<Identifier>;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { reset } = useFormContext();
  const { refetch } = useListContext();

  if (!record || !identity) return null;

  const handleSuccess = () => {
    reset({ date: todayIsoDate(), text: "" }, { keepValues: false });
    refetch();
    notify("resources.notes.added");
  };

  return (
    <div className="flex justify-end">
      <SaveButton
        type="button"
        label="resources.notes.action.add_this"
        transform={(data) => ({
          ...data,
          [foreignKeyMapping[reference]]: record.id,
          sales_id: identity.id,
          date: toNoteDate(data.date),
        })}
        mutationOptions={{ onSuccess: handleSuccess }}
      />
    </div>
  );
};
